import { designDocumentSchema, productConfigSchema } from '../domain/schemas';
import type { DesignDocument, ProductConfig } from '../domain/types';

export const LUCASART_CONFIGURATION_SCHEMA = 'lucasart.configuration/v1' as const;

export interface LucasArtStoredConfiguration {
  schemaId: typeof LUCASART_CONFIGURATION_SCHEMA;
  design: DesignDocument;
  product?: ProductConfig;
}

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function parseJsonValue(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return value;
  }
}

function parseDesign(value: unknown): DesignDocument | null {
  const result = designDocumentSchema.safeParse(parseJsonValue(value));
  return result.success ? (result.data as DesignDocument) : null;
}

function parseProduct(value: unknown): ProductConfig | null {
  const result = productConfigSchema.safeParse(parseJsonValue(value));
  return result.success ? (result.data as ProductConfig) : null;
}

export function createStoredConfiguration(
  design: DesignDocument,
  product: ProductConfig,
): LucasArtStoredConfiguration {
  return {
    schemaId: LUCASART_CONFIGURATION_SCHEMA,
    design,
    product,
  };
}

/**
 * Unwraps both the canonical LucasArt payload and the envelopes used by the
 * generic Eliadmin product-configurator API. JSON strings are accepted because
 * older records may store their configuration output as serialized JSON.
 */
export function extractStoredConfiguration(
  payload: unknown,
  depth = 0,
): LucasArtStoredConfiguration | null {
  if (depth > 8) return null;
  const parsed = parseJsonValue(payload);

  if (Array.isArray(parsed)) {
    for (const item of parsed) {
      const candidate = extractStoredConfiguration(item, depth + 1);
      if (candidate) return candidate;
    }
    return null;
  }

  const directDesign = parseDesign(parsed);
  if (directDesign) {
    return { schemaId: LUCASART_CONFIGURATION_SCHEMA, design: directDesign };
  }
  if (!isRecord(parsed)) return null;

  const design = parseDesign(
    parsed.design ?? parsed.designDocument ?? parsed.design_document ?? parsed.configurationDesign,
  );
  if (design) {
    const product = parseProduct(
      parsed.product ?? parsed.productSnapshot ?? parsed.product_snapshot ?? parsed.productConfig,
    );
    return {
      schemaId: LUCASART_CONFIGURATION_SCHEMA,
      design,
      ...(product ? { product } : {}),
    };
  }

  for (const key of [
    'configuration',
    'config',
    'state',
    'configurationPayload',
    'configuration_payload',
    'payload',
    'content',
    'configuration_output',
    'configurationOutput',
    'generatedConfiguration',
    'attributes',
    'selectedOptions',
    'data',
  ]) {
    const candidate = extractStoredConfiguration(parsed[key], depth + 1);
    if (!candidate) continue;
    if (candidate.product) return candidate;
    const product = parseProduct(
      parsed.product ?? parsed.productSnapshot ?? parsed.product_snapshot ?? parsed.productConfig,
    );
    return product ? { ...candidate, product } : candidate;
  }

  return null;
}
